// Search asset API client — mirrors campaigns.ts pattern but targets /search-assets/* endpoints.
// Stubs for Milestone 1 (types only). Full implementation in Milestone 2.

import client from './client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SearchAsset {
  id: string;
  asset_name: string;
  asset_type: string | null;
  status: 'draft' | 'approved';
  storage_path: string | null;
  file_size: number | null;
  content_type: string | null;
  video_length: string | null;
  ratio: string | null;
  variant_number: number;

  // Text fields
  headlines: string[] | null;
  descriptions: string[] | null;
  cta: string[] | null;
  legal: string | null;

  // Search-specific fields
  long_headlines: string[] | null;
  business_name: string | null;
  display_url_paths: string[] | null;
  final_url: string | null;

  // Offer-led (per variant)
  is_offer_led?: boolean | null;
  offer_end_date?: string | null;

  // Timestamps
  created_at: string | null;
  updated_at: string | null;

  // Approval
  approved_by_user_id: string | null;
  approved_at: string | null;
  approval_note: string | null;
  validation_result: {
    is_valid: boolean;
    errors: { field: string; message: string; rule: string }[];
    warnings: { field: string; message: string; rule: string }[];
  } | null;
  approved_by: { name: string; email: string } | null;

  // Multi-file storage
  media_files: MediaFileEntry[];

  // Sitelinks (Search & PMax only) — up to 10
  sitelinks: SitelinkEntry[];

  // Lock status
  is_locked: boolean;

  // Signed URL (when include_urls=true)
  signed_url?: string | null;
}

export interface SitelinkEntry {
  title: string;
  description1: string;
  description2: string;
  final_url: string;
}

export interface MediaFileEntry {
  id: string;
  slot: string;
  storage_path: string;
  content_type: string;
  file_size: number;
  source_fordshare_id?: string | null;
  expiration_date?: string | null;
  signed_url?: string | null;
  width?: number;
  height?: number;
}

export interface SearchAssetsResponse {
  campaign_id: string;
  total_assets: number;
  assets: SearchAsset[];
}

export interface SearchAssetSubmissionData {
  headlines?: string[];
  long_headlines?: string[];
  descriptions?: string[];
  cta?: string[];
  legal?: string;
  business_name?: string;
  display_url_paths?: string[];
  final_url?: string;
  variant_number?: number;
  // Offer-led (per variant). Omitting `is_offer_led` leaves the stored values
  // untouched — the backend only writes when the flag is present.
  is_offer_led?: boolean;
  offer_end_date?: string;
  // File upload refs (from signed URL upload flow)
  storage_path?: string;
  content_type?: string;
  file_size?: number;
  // Multi-file array
  media_files?: MediaFileEntry[];
  // Sitelinks (Search & PMax only)
  sitelinks?: SitelinkEntry[];
  // Skip backend validation on save-only (validate separately)
  skip_validation?: boolean;
}

export interface SearchAssetSubmissionResponse {
  success: boolean;
  campaign_id: string;
  asset_ids: string[];
  message: string;
  new_version?: number;
}

export interface ApproveSearchAssetsResponse {
  success: boolean;
  campaign_id: string;
  assets_approved: number;
  variants_approved: number[];
  force_approved: boolean;
  message: string;
}

// ─── API functions ──────────────────────────────────────────────────────────

export async function getSearchAssets(
  campaignId: string,
  includeUrls: boolean = true,
): Promise<SearchAssetsResponse> {
  const response = await client.get<SearchAssetsResponse>(
    `/campaigns/${campaignId}/search-assets`,
    { params: { include_urls: includeUrls } },
  );
  return response.data;
}

export async function submitSearchAssets(
  campaignId: string,
  data: SearchAssetSubmissionData,
): Promise<SearchAssetSubmissionResponse> {
  const formData = new FormData();

  // Always send text fields — even empty arrays/strings — so the backend
  // can clear previously saved values (e.g. user deletes business name).
  formData.append('headlines', JSON.stringify(data.headlines ?? []));
  formData.append('long_headlines', JSON.stringify(data.long_headlines ?? []));
  formData.append('descriptions', JSON.stringify(data.descriptions ?? []));
  formData.append('cta', JSON.stringify(data.cta ?? []));
  formData.append('display_url_paths', JSON.stringify(data.display_url_paths ?? []));
  formData.append('legal', data.legal ?? '');
  formData.append('business_name', data.business_name ?? '');
  formData.append('final_url', data.final_url ?? '');
  formData.append('variant_number', String(data.variant_number ?? 1));
  if (data.is_offer_led !== undefined) {
    formData.append('is_offer_led', data.is_offer_led.toString());
    formData.append('offer_end_date', data.offer_end_date ?? '');
  }

  // Skip validation flag — append whenever explicitly provided
  if (data.skip_validation !== undefined) {
    formData.append('skip_validation', String(data.skip_validation));
  }

  // File fields — only sent when there's an upload or explicit clear
  if (data.storage_path !== undefined) formData.append('storage_path', data.storage_path);
  if (data.content_type !== undefined) formData.append('content_type', data.content_type);
  if (data.file_size !== undefined) formData.append('file_size', String(data.file_size));

  // Multi-file array — always send when present
  if (data.media_files !== undefined)
    formData.append('media_files', JSON.stringify(data.media_files));

  // Sitelinks — always send when present so the backend can clear removed rows
  if (data.sitelinks !== undefined) formData.append('sitelinks', JSON.stringify(data.sitelinks));

  const response = await client.post<SearchAssetSubmissionResponse>(
    `/campaigns/${campaignId}/search-assets/submit`,
    formData,
  );
  return response.data;
}

export async function deleteSearchVariant(
  campaignId: string,
  variantNumber: number,
): Promise<{ success: boolean; message: string }> {
  const response = await client.delete(
    `/campaigns/${campaignId}/search-assets/variant/${variantNumber}`,
  );
  return response.data;
}

export async function duplicateSearchVersion(
  campaignId: string,
  sourceVersion: number,
): Promise<SearchAssetSubmissionResponse> {
  const response = await client.post<SearchAssetSubmissionResponse>(
    `/campaigns/${campaignId}/search-assets/duplicate`,
    { source_version: sourceVersion },
  );
  return response.data;
}

export async function approveAllSearchVariants(
  campaignId: string,
  approvalNote?: string,
): Promise<ApproveSearchAssetsResponse> {
  const response = await client.post<ApproveSearchAssetsResponse>(
    `/campaigns/${campaignId}/search-assets/approve-all`,
    { approval_note: approvalNote },
  );
  return response.data;
}
